import { downloadBlob, showToast } from 'BreetHelpers';

export interface IEngagementActions {
	isResolve?: boolean;
	onResolve?: () => void;
	shareContentDetails?: IShareContent;
	noReport?: boolean;
	isCopy?: boolean;
	copy?: {
		item: string;
		message: string;
		copyFn?: () => void;
	};
}

export interface IShareContent {
	title: string;
	text?: string;
	url?: string;
	screenshotElementId?: string;
	action?: 'share' | 'download';
}

const RECEIPT_WIDTH_PX = 457;
const RECEIPT_PADDING_PX = 30;
const RECEIPT_FILE_NAME = 'breet-receipt';

const captureReceipt = async (elementId: string): Promise<HTMLCanvasElement> => {
	const html2canvas = (await import('html2canvas')).default;
	const source = document.getElementById(elementId);
	if (!source) throw new Error('Element not found');

	const frame = document.createElement('div');
	frame.className = 'screenshot-mode';
	Object.assign(frame.style, {
		position: 'fixed',
		top: '0',
		left: '-9999px',
		width: `${RECEIPT_WIDTH_PX}px`,
		padding: `${RECEIPT_PADDING_PX}px`,
		background: 'white',
		boxSizing: 'content-box',
		overflow: 'visible',
		zIndex: '-1',
	});

	const clone = source.cloneNode(true) as HTMLElement;
	clone.removeAttribute('id');

	clone.querySelectorAll<SVGSVGElement>('svg[data-testid="status-icon"]').forEach((svg) => {
		const width = svg.getAttribute('width') ?? '0';
		const height = svg.getAttribute('height') ?? '24';
		const markup = new XMLSerializer().serializeToString(svg);
		const img = document.createElement('img');
		img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
		img.width = Number(width);
		img.height = Number(height);
		svg.replaceWith(img);
	});

	const footer = document.createElement('div');
	Object.assign(footer.style, {
		marginTop: '2rem',
		paddingTop: '1.6rem',
		borderTop: '0.1rem solid #e5e7eb',
		textAlign: 'center',
		fontSize: '1.2rem',
		lineHeight: '1.6rem',
		color: '#6b7280',
	});
	footer.textContent = 'Generated by Breet Business · business.breet.io';

	frame.append(clone, footer);
	document.body.appendChild(frame);

	try {
		await new Promise((res) => requestAnimationFrame(res));
		return await html2canvas(frame, {
			useCORS: true,
			allowTaint: false,
			scale: 4,
			backgroundColor: '#ffffff',
			onclone: (clonedDoc) => clonedDoc.documentElement.setAttribute('data-theme', 'light'),
		});
	} finally {
		frame.remove();
	}
};

const canvasToFile = (canvas: HTMLCanvasElement, mimeType: string, fileName: string): Promise<File> =>
	new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (!blob) {
					reject(new Error('Failed to render receipt'));
					return;
				}
				resolve(new File([blob], fileName, { type: mimeType }));
			},
			mimeType,
			mimeType === 'image/jpeg' ? 0.95 : undefined
		);
	});

const deflate = async (bytes: Uint8Array): Promise<Uint8Array> => {
	const source = new Response(bytes).body;
	if (!source) throw new Error('Compression stream unavailable');

	const compressed = source.pipeThrough(new CompressionStream('deflate'));
	return new Uint8Array(await new Response(compressed).arrayBuffer());
};

const canvasToRgb = (canvas: HTMLCanvasElement): Uint8Array => {
	const ctx = canvas.getContext('2d');
	if (!ctx) throw new Error('Canvas context unavailable');

	const { width, height } = canvas;
	const rgba = ctx.getImageData(0, 0, width, height).data;
	const rgb = new Uint8Array(width * height * 3);
	for (let i = 0, j = 0; i < rgba.length; i += 4, j += 3) {
		rgb[j] = rgba[i];
		rgb[j + 1] = rgba[i + 1];
		rgb[j + 2] = rgba[i + 2];
	}
	return rgb;
};

const buildImagePdf = (
	image: Uint8Array,
	filter: 'FlateDecode' | 'DCTDecode',
	widthPx: number,
	heightPx: number,
	pageWidthPx = widthPx,
	pageHeightPx = heightPx
): Blob => {
	const encoder = new TextEncoder();
	const pageWidth = +((pageWidthPx * 72) / 96).toFixed(2);
	const pageHeight = +((pageHeightPx * 72) / 96).toFixed(2);

	const chunks: Uint8Array[] = [];
	const offsets: number[] = [];
	let length = 0;

	const push = (data: string | Uint8Array) => {
		const bytes = typeof data === 'string' ? encoder.encode(data) : data;
		chunks.push(bytes);
		length += bytes.length;
	};
	const startObject = () => offsets.push(length);

	push('%PDF-1.3\n');
	push(new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));

	startObject();
	push('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

	startObject();
	push('2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n');

	startObject();
	push(
		`3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] ` +
			'/Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>\nendobj\n'
	);

	startObject();
	push(
		`4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${widthPx} /Height ${heightPx} ` +
			`/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /${filter} /Length ${image.length} >>\nstream\n`
	);
	push(image);
	push('\nendstream\nendobj\n');

	const content = `q ${pageWidth} 0 0 ${pageHeight} 0 0 cm /Im0 Do Q`;
	startObject();
	push(`5 0 obj\n<< /Length ${content.length} >>\nstream\n${content}\nendstream\nendobj\n`);

	const xrefStart = length;
	let xref = `xref\n0 ${offsets.length + 1}\n0000000000 65535 f \n`;
	for (const offset of offsets) xref += `${String(offset).padStart(10, '0')} 00000 n \n`;
	push(xref);
	push(`trailer\n<< /Size ${offsets.length + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF\n`);

	return new Blob(chunks, { type: 'application/pdf' });
};

const copyImageToClipboard = async (file: File): Promise<boolean> => {
	if (typeof ClipboardItem === 'undefined' || !('clipboard' in navigator)) return false;

	try {
		await navigator.clipboard.write([new ClipboardItem({ [file.type]: file })]);
		return true;
	} catch {
		return false;
	}
};

const handleShareAction = async ({
	title,
	text,
	url,
	screenshotFile,
}: {
	title?: string;
	text?: string;
	url?: string;
	screenshotFile?: File;
}) => {
	if (screenshotFile && navigator.canShare({ files: [screenshotFile] })) {
		try {
			await navigator.share({ title, text, files: [screenshotFile] });
			return;
		} catch (error) {
			if ((error as Error).name === 'NotAllowedError') {
				showToast('success', 'Downloading instead...');
				downloadBlob(screenshotFile, `${RECEIPT_FILE_NAME}.png`);
				return;
			}
			throw error;
		}
	}

	await navigator.share({ title, text, url });
};

const handleShareError = (error: unknown) => {
	if ((error as Error).name === 'AbortError') return;
	showToast('error', 'Couldn’t share the receipt. Please try again.');
};

const prepareReceiptCanvas = async (elementId: string): Promise<HTMLCanvasElement | undefined> => {
	showToast('success', 'Preparing receipt...');
	try {
		return await captureReceipt(elementId);
	} catch {
		showToast('error', 'Couldn’t generate the receipt. Please try again.');
		return undefined;
	}
};

export const shareContent = (shareContentArg?: IShareContent) => async () => {
	if (!shareContentArg) return;

	try {
		const { title, text, url, screenshotElementId } = shareContentArg;

		const canvas = screenshotElementId ? await prepareReceiptCanvas(screenshotElementId) : undefined;
		if (screenshotElementId && !canvas) return;

		const screenshotFile = canvas ? await canvasToFile(canvas, 'image/png', `${RECEIPT_FILE_NAME}.png`) : undefined;

		if (screenshotFile && 'share' in navigator && navigator.canShare({ files: [screenshotFile] })) {
			await handleShareAction({ title, text, url, screenshotFile });
			return;
		}

		if (screenshotFile) {
			if (await copyImageToClipboard(screenshotFile)) {
				showToast('success', 'Receipt copied — paste it anywhere');
				return;
			}

			downloadBlob(screenshotFile, `${RECEIPT_FILE_NAME}.png`);
			return;
		}

		if ('share' in navigator) await handleShareAction({ title, text, url });
	} catch (error) {
		handleShareError(error);
	}
};

/** Copy the receipt image to the clipboard (desktop share-menu option). */
export const copyReceiptImage = (shareContentArg?: IShareContent) => async () => {
	if (!shareContentArg?.screenshotElementId) return;

	const canvas = await prepareReceiptCanvas(shareContentArg.screenshotElementId);
	if (!canvas) return;

	const file = await canvasToFile(canvas, 'image/png', `${RECEIPT_FILE_NAME}.png`);
	if (await copyImageToClipboard(file)) {
		showToast('success', 'Receipt copied — paste it anywhere');
		return;
	}

	showToast('error', 'Couldn’t copy the receipt. Downloading instead...');
	downloadBlob(file, `${RECEIPT_FILE_NAME}.png`);
};

export const downloadReceiptImage = (shareContentArg?: IShareContent) => async () => {
	if (!shareContentArg?.screenshotElementId) return;

	const canvas = await prepareReceiptCanvas(shareContentArg.screenshotElementId);
	if (!canvas) return;

	const file = await canvasToFile(canvas, 'image/png', `${RECEIPT_FILE_NAME}.png`);
	downloadBlob(file, `${RECEIPT_FILE_NAME}.png`);
};

const CAPTURE_SCALE = 4;

const canvasToPdf = async (canvas: HTMLCanvasElement, scale = CAPTURE_SCALE): Promise<Blob> => {
	const logicalWidth = canvas.width / scale;
	const logicalHeight = canvas.height / scale;

	if (typeof CompressionStream !== 'undefined') {
		try {
			const deflated = await deflate(canvasToRgb(canvas));
			return buildImagePdf(deflated, 'FlateDecode', canvas.width, canvas.height, logicalWidth, logicalHeight);
		} catch (error) {
			console.error('Lossless PDF generation failed, falling back to JPEG:', error);
		}
	}

	const jpegFile = await canvasToFile(canvas, 'image/jpeg', `${RECEIPT_FILE_NAME}.jpg`);
	const jpegBytes = new Uint8Array(await jpegFile.arrayBuffer());
	return buildImagePdf(jpegBytes, 'DCTDecode', canvas.width, canvas.height, logicalWidth, logicalHeight);
};

export const downloadReceiptPdf = (shareContentArg?: IShareContent) => async () => {
	if (!shareContentArg?.screenshotElementId) return;

	const canvas = await prepareReceiptCanvas(shareContentArg.screenshotElementId);
	if (!canvas) return;

	const pdf = await canvasToPdf(canvas);
	downloadBlob(pdf, `${RECEIPT_FILE_NAME}.pdf`);
};
